package unlock

import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException
import kotlinx.browser.window
import kotlinx.coroutines.suspendCancellableCoroutine
import org.khronos.webgl.Int8Array

/**
 * Her phone's copy of the opened gift (T16): one IndexedDB record holding [puzzlekey.Unlock.keepsake].
 * iOS drops tabs freely (app switch, camera, memory), and the reveal must not be lost with it.
 * Private browsing or blocked storage throws: the callers treat that as nothing kept.
 */
object KeptGift {
    private const val DATABASE = "gift"
    private const val STORE = "kept"
    private const val KEY = "gift"

    suspend fun put(keepsake: ByteArray) {
        val db = open()
        try {
            val transaction = db.transaction(STORE, "readwrite")
            transaction.objectStore(STORE).put(keepsake, KEY)
            committed(transaction)
        } finally {
            db.close()
        }
    }

    /**
     * Drops the kept gift, so the next load starts with the hunt (his rehearsal, T17). IndexedDB queues
     * later opens of the database behind the delete, so [get] right after it finds nothing.
     */
    fun forget() {
        window.asDynamic().indexedDB.deleteDatabase(DATABASE)
    }

    suspend fun get(): ByteArray? {
        val db = open()
        try {
            val kept = request(db.transaction(STORE, "readonly").objectStore(STORE).get(KEY))
            return if (kept is Int8Array) kept.unsafeCast<ByteArray>() else null
        } finally {
            db.close()
        }
    }

    private suspend fun open(): dynamic {
        val opening = window.asDynamic().indexedDB.open(DATABASE, 1)
        opening.onupgradeneeded = { opening.result.createObjectStore(STORE) }
        return request(opening)
    }

    /** Waits until [transaction] has committed, not only been queued. */
    private suspend fun committed(transaction: dynamic): Unit = suspendCancellableCoroutine { continuation ->
        transaction.oncomplete = { continuation.resume(Unit) }
        // A failed write fires error, then abort: resume once.
        val failed = { if (continuation.isActive) continuation.resumeWithException(Exception("IndexedDB: ${transaction.error?.message ?: "aborted"}")) }
        transaction.onerror = failed
        transaction.onabort = failed
    }

    private suspend fun request(request: dynamic): dynamic = suspendCancellableCoroutine { continuation ->
        request.onsuccess = { continuation.resume(request.result) }
        request.onerror = { continuation.resumeWithException(Exception("IndexedDB: ${request.error?.message ?: request.error}")) }
    }
}
